/**
 * Keycloak OIDC 인증 설정 및 유틸리티
 *
 * SPA 모드: oidc-client-ts 를 브라우저에서 직접 사용 (Authorization Code + PKCE).
 * - 신뢰 경계는 서버(Keycloak resource server). FE 인증은 UX 보조.
 * - access_token 은 이 모듈 스코프가 단일 보관(getAccessToken/setAccessToken).
 *   컴포넌트는 토큰에 직접 접근하지 않고 api-client(apiFetch)가 헤더에 주입.
 * - 시각은 서버 권위(GxP/ALCOA+). 클라이언트 Date.now() 로 감사/서명 시각 생성 금지.
 */

import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";

/**
 * 현재 사용자 세션 정보
 */
export interface SessionUser {
  sub: string;
  email: string;
  name: string;
  /** Keycloak realm roles (RBAC 출처) */
  roles: string[];
  /** 역할 표시 이름 (roles.name, 예: "CompanyAdmin") — 표시는 roleLabel(roleCode, roleName) 로 번역해서 씀. nullable */
  roleName?: string;
  /** 역할 코드 (roles.code, 예: "COMPANY_ADMIN") — 관리자 판단·표시 번역 기준. 사용자 역할이면 없음 */
  roleCode?: string;
  /** 직책명 (예: "품질책임자(QA)") — 헤더 사용자 메뉴 표시용(직책 = job_positions.name). nullable */
  positionName?: string;
  /** 현재 활성 회사(tenant) slug */
  companySlug: string;
  /**
   * 사이트 관리자(회사 어드민) 여부 — 시스템 설정(사용자·역할·코드 등) 메뉴 노출 게이트.
   * 서버 권위(/auth/me siteAdmin) 출처. 신뢰 경계는 서버 — UI 노출 보조용.
   */
  isSiteAdmin?: boolean;
  /** 슈퍼관리자(공급사) 여부 — 모듈 프로비저닝(테넌트 전역) 메뉴 노출 게이트. 서버 권위. */
  isSuperAdmin?: boolean;
  /**
   * 비밀번호 만료 여부(전역 보안정책) — 서버 권위(/auth/me passwordExpired). 로그인이 ROPC라 백엔드가 로그인 경로에 없어
   * /me 사후 검사로 만료를 판정함. true 면 FE가 비밀번호 변경을 유도함.
   */
  passwordExpired?: boolean;
}

/**
 * RBAC 검사 - UX 보조용 (신뢰 경계 아님, 서버에서 재검증)
 */
export function hasRole(user: SessionUser, required: string | string[]): boolean {
  const requiredRoles = Array.isArray(required) ? required : [required];
  return requiredRoles.some((role) => user.roles.includes(role));
}

/**
 * Keycloak OIDC 설정 상수
 */
export const OIDC_CONFIG = {
  authority: import.meta.env.VITE_KEYCLOAK_URL ?? "http://localhost:8080/realms/eqms",
  client_id: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? "eqms-web",
  redirect_uri: `${import.meta.env.VITE_APP_URL ?? "http://localhost:3000"}/auth/callback`,
  scope: "openid profile email",
} as const;

/**
 * 현재 access_token 보관소 (모듈 스코프)
 *
 * OIDC 레이어(UserManager)가 로그인/갱신 시 setAccessToken() 으로 적재함.
 * api-client.ts apiFetch 가 Authorization 헤더 첨부 시 getAccessToken() 으로 조회함.
 */
let currentAccessToken: string | null = null;

/**
 * dev 폼 로그인 토큰의 sessionStorage 키.
 * dev 환경은 Keycloak(oidc-client) 세션 저장소가 없으므로, 새로고침 시 로그인이 풀리지
 * 않도록 식별 토큰(= 사용자 ID)을 탭 단위(sessionStorage)로 보관·복원함.
 */
const DEV_TOKEN_KEY = "eqms.dev.accessToken";

/**
 * KEYCLOAK_DIRECT(ROPC) 모드 — 앱 자체 로그인 폼으로 Keycloak Direct Access Grant(grant_type=password)
 * 인증. Keycloak 로그인 페이지로 리다이렉트하지 않고 앱 폼에서 받은 자격증명을 토큰 엔드포인트로 직접 교환함.
 * ⚠️ 운영(GxP/Part 11)은 리다이렉트(Authorization Code)가 권장 — IdP가 자격증명을 소유. 단 HTTP 운영 중에는
 * 브라우저가 PKCE 용 crypto.subtle 을 막아 리다이렉트가 불가하므로 이 모드를 쓴다(docs/README.md 배포 인프라).
 * 토큰(JWT)은 만료가 짧으므로 refresh_token 으로 자동 갱신함.
 */
const KC_ACCESS_KEY = "eqms.kc.accessToken";
const KC_REFRESH_KEY = "eqms.kc.refreshToken";
let kcRefreshTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * OIDC 레이어/폼 로그인이 토큰 적재·만료 시 호출.
 * dev 모킹 환경에서는 sessionStorage 에도 동기화해 새로고침 후 세션을 복원함.
 */
export function setAccessToken(token: string | null): void {
  currentAccessToken = token;
  if (typeof window === "undefined") return;
  if (DEV_MOCK_AUTH) {
    if (token) window.sessionStorage.setItem(DEV_TOKEN_KEY, token);
    else window.sessionStorage.removeItem(DEV_TOKEN_KEY);
  } else if (KEYCLOAK_DIRECT) {
    if (token) window.sessionStorage.setItem(KC_ACCESS_KEY, token);
    else window.sessionStorage.removeItem(KC_ACCESS_KEY);
  }
}

/** apiFetch 가 Authorization 헤더 첨부 시 조회. 없으면 null */
export function getAccessToken(): string | null {
  return currentAccessToken;
}

/** dev 환경에서 sessionStorage 에 보관된 토큰 복원(모듈 스코프 적재). 없으면 null. */
function restoreDevToken(): string | null {
  if (!DEV_MOCK_AUTH || typeof window === "undefined") {
    return null;
  }
  const token = window.sessionStorage.getItem(DEV_TOKEN_KEY);
  if (token) {
    currentAccessToken = token;
  }
  return token;
}

// ---------------------------------------------------------------------------
// UserManager (lazy 초기화)
// ---------------------------------------------------------------------------

/** dev 인증 우회 여부 (운영 빌드에서 반드시 false) */
export const DEV_MOCK_AUTH = import.meta.env.VITE_DEV_MOCK_AUTH === "true";

/**
 * Keycloak 직접(ROPC) 로그인 모드 — 앱 폼(아이디/비번)으로 Keycloak 인증(리다이렉트 없음).
 * false 면 리다이렉트(Authorization Code) 플로우 사용. DEV_MOCK_AUTH 가 우선(둘 다여도 mock).
 */
export const KEYCLOAK_DIRECT =
  !DEV_MOCK_AUTH && import.meta.env.VITE_KEYCLOAK_DIRECT_LOGIN === "true";

let userManager: UserManager | null = null;

/**
 * UserManager 싱글턴 lazy 초기화.
 * 토큰은 sessionStorage 에 보관(탭 단위 격리). silent renew 활성화.
 */
export function getUserManager(): UserManager {
  if (!userManager) {
    userManager = new UserManager({
      authority: OIDC_CONFIG.authority,
      client_id: OIDC_CONFIG.client_id,
      redirect_uri: OIDC_CONFIG.redirect_uri,
      scope: OIDC_CONFIG.scope,
      response_type: "code",
      automaticSilentRenew: true,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    });

    // 토큰 갱신/만료 시 모듈 스코프 토큰 동기화
    userManager.events.addUserLoaded((user) => {
      setAccessToken(user.access_token ?? null);
    });
    userManager.events.addUserUnloaded(() => {
      setAccessToken(null);
    });
    userManager.events.addAccessTokenExpired(() => {
      setAccessToken(null);
    });
  }
  return userManager;
}

/**
 * dev 폼 로그인 응답 형태 (BE POST /auth/login). DevAuthController.LoginResponse 계약.
 */
interface DevLoginResponse {
  /** dev 식별 토큰(= 사용자 ID). 이후 요청 Authorization: Bearer 로 전달. */
  accessToken: string;
  id: string;
  companyId: string;
  email: string;
  name: string;
  roleId?: string | null;
  roleName?: string | null;
  companySlug?: string | null;
}

/** DevLoginResponse → SessionUser 매핑(역할은 roleId/roleName 으로 구성 — UX 보조). */
function devLoginToSessionUser(res: DevLoginResponse): SessionUser {
  const roles = [res.roleId, res.roleName].filter((r): r is string => Boolean(r));
  return {
    sub: res.id,
    email: res.email,
    name: res.name,
    roles,
    roleName: res.roleName ?? undefined,
    companySlug: res.companySlug ?? res.companyId ?? "",
  };
}

/**
 * dev 폼 로그인 실패 에러 — HTTP 상태를 실어 호출부(LoginPage)가 사유별 안내를 하도록 함.
 * 401 = 자격 불일치(계정 존재 비노출), 403 = 이메일 미인증(진입 차단, D-06), 423 = 실패 초과 계정 잠금.
 *
 * @param lockoutMinutes 423 일 때 자동해제까지 남은 분(서버 전역 보안정책 설정값). 0/undefined = 자동해제 없음.
 *   잠금 시간은 설정 가능한 값이므로 화면 문구에 하드코딩하지 않고 이 값을 표시함.
 * @param tempLock 423 중 Keycloak 일시 잠금(비밀번호 연속 실패, ACCOUNT_TEMP_LOCKED)일 때 허용 실패 횟수·남은 초.
 *   보안정책 잠금(lockoutMinutes)과 별개.
 */
export class LoginError extends Error {
  constructor(
    public readonly status: number,
    public readonly lockoutMinutes?: number,
    public readonly tempLock?: { failureLimit: number; retryAfterSeconds: number },
  ) {
    super(`login failed: ${status}`);
    this.name = "LoginError";
  }
}

/**
 * 로그인 시 비밀번호는 맞으나 2단계 인증(TOTP) 코드가 필요함(등록자, 코드 미제출). FE 는 이때만 코드 입력 단계를 노출함.
 */
export class MfaRequiredError extends Error {
  constructor() {
    super("mfa required");
    this.name = "MfaRequiredError";
  }
}

/**
 * 서버에 연결할 수 없음 — 네트워크 예외(fetch 실패) 또는 5xx(502/503/504 등 서버·게이트웨이 다운).
 * 자격증명 실패(401)·인증 미완(403)과 구분해, 호출부가 "비밀번호 오류"가 아니라 "서버 다운"으로 안내하도록 함.
 */
export class ServerUnavailableError extends Error {
  constructor() {
    super("server unavailable");
    this.name = "ServerUnavailableError";
  }
}

/**
 * 로그인 요청 fetch 래퍼 — 네트워크 예외/5xx 를 ServerUnavailableError 로 정규화함.
 * 서버가 죽으면 fetch 자체가 throw(TypeError) 하거나 게이트웨이가 5xx 를 돌려주는데, 그대로 두면
 * 호출부(LoginPage)에서 자격증명 오류로 오인됨. 4xx(401/403 등)는 그대로 통과시켜 기존 분기를 유지함.
 */
/**
 * 로그인 실패 응답 → LoginError. 423(LOCKED) 응답의 RFC 9457 확장 멤버를 함께 추출함:
 * {@code lockoutMinutes}(보안정책 잠금 자동해제까지 남은 분) · {@code failureLimit}/{@code retryAfterSeconds}(Keycloak
 * 일시 잠금의 허용 실패 횟수·남은 초). 잠금 시간은 설정값이라 서버가 알려주는 값만 신뢰한다(FE 하드코딩 금지).
 * 본문이 없거나 형식이 다르면 상태만 실음 — 호출부는 시간 없는 일반 잠금 안내로 폴백함.
 */
async function readLoginError(res: Response): Promise<LoginError> {
  try {
    const body = (await res.json()) as {
      lockoutMinutes?: unknown;
      failureLimit?: unknown;
      retryAfterSeconds?: unknown;
    };
    const lockoutMinutes =
      typeof body.lockoutMinutes === "number" ? body.lockoutMinutes : undefined;
    // Keycloak 일시 잠금(비밀번호 연속 실패) — 허용 횟수·남은 초.
    const tempLock =
      typeof body.failureLimit === "number" && typeof body.retryAfterSeconds === "number"
        ? { failureLimit: body.failureLimit, retryAfterSeconds: body.retryAfterSeconds }
        : undefined;
    return new LoginError(res.status, lockoutMinutes, tempLock);
  } catch {
    return new LoginError(res.status);
  }
}

async function loginFetch(input: string, init: RequestInit): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(input, init);
  } catch {
    throw new ServerUnavailableError();
  }
  if (res.status >= 500) throw new ServerUnavailableError();
  return res;
}

/**
 * dev 폼 로그인 — 이메일 + 비밀번호를 BE(POST /auth/login)에 보내 BCrypt 검증.
 *
 * - 운영(prod)은 Keycloak(signinRedirect 또는 KEYCLOAK_DIRECT 폼)을 사용하므로 본 함수는 dev 전용임.
 * - 성공 시 식별 토큰(= 사용자 ID)을 setAccessToken() 으로 적재(sessionStorage 동기화).
 * - api-client(apiFetch) 순환 import 를 피하기 위해 fetch 를 직접 사용함.
 *
 * @throws LoginError 인증 실패(401 자격 불일치 / 403 이메일 미인증) 또는 네트워크 오류
 */
export async function devLogin(email: string, password: string): Promise<SessionUser> {
  const base = import.meta.env.VITE_API_URL ?? "";
  // 네트워크 예외/5xx 는 loginFetch 가 ServerUnavailableError 로 정규화(서버 다운 안내). 4xx 는 통과.
  const response = await loginFetch(`${base}/api/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    // 401 = 자격 불일치(계정 존재 비노출), 403 = 이메일 미인증. 상태를 실어 호출부가 안내를 분기.
    throw new LoginError(response.status);
  }

  const data = (await response.json()) as DevLoginResponse;
  setAccessToken(data.accessToken);
  // 로그인 응답(LoginResponse)에는 positionName·siteAdmin 이 없어, 그대로 스토어에 넣으면
  // 헤더 직책 라벨이 역할명으로 폴백되고 isSiteAdmin 미설정으로 "시스템 설정"
  // 메뉴 게이트가 닫힘. 토큰 적재 직후 /auth/me 로 전체 사용자(positionName·siteAdmin 포함)를
  // 복원해 스토어가 완전한 세션을 참조하게 함. /me 실패 시 로그인 응답 매핑으로 폴백.
  const full = await getUser();
  return full ?? devLoginToSessionUser(data);
}

/** BE /api/auth/me 응답 → SessionUser. 폼 토큰(dev·Keycloak ROPC) 세션 복원 공용. */
async function fetchMeAsSessionUser(token: string): Promise<SessionUser | null> {
  const base = import.meta.env.VITE_API_URL ?? "";
  const response = await fetch(`${base}/api/auth/me`, {
    headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
  });
  if (!response.ok) return null;
  const me = (await response.json()) as {
    id?: string;
    email?: string;
    name?: string;
    roleId?: string | null;
    roleName?: string | null;
    roleCode?: string | null;
    positionName?: string | null;
    companyId?: string | null;
    siteAdmin?: boolean;
    superAdmin?: boolean;
    passwordExpired?: boolean;
  };
  const roles = [me.roleId, me.roleName].filter((r): r is string => Boolean(r));
  return {
    sub: me.id ?? "",
    email: me.email ?? "",
    name: me.name ?? "",
    roles,
    roleName: me.roleName ?? undefined,
    roleCode: me.roleCode ?? undefined,
    positionName: me.positionName ?? undefined,
    companySlug: me.companyId ?? "",
    isSiteAdmin: me.siteAdmin ?? undefined,
    isSuperAdmin: me.superAdmin ?? undefined,
    passwordExpired: me.passwordExpired ?? undefined,
  };
}

// ---------------------------------------------------------------------------
// Keycloak 직접(ROPC) 로그인 — 앱 폼 인증 + refresh_token 자동 갱신
// ---------------------------------------------------------------------------

/** Keycloak 토큰 엔드포인트. */
function kcTokenEndpoint(): string {
  return `${OIDC_CONFIG.authority}/protocol/openid-connect/token`;
}

interface KcTokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
}

/** 토큰 보관 + 만료 30초 전 자동 갱신 예약(활성 탭 세션 유지). */
function storeKcTokens(data: KcTokenResponse): void {
  setAccessToken(data.access_token); // currentAccessToken + KC_ACCESS_KEY 동기화
  if (typeof window !== "undefined" && data.refresh_token) {
    window.sessionStorage.setItem(KC_REFRESH_KEY, data.refresh_token);
  }
  if (typeof window !== "undefined") {
    if (kcRefreshTimer) clearTimeout(kcRefreshTimer);
    const delay = Math.max(5, (data.expires_in ?? 300) - 30) * 1000;
    kcRefreshTimer = setTimeout(() => void refreshKeycloakToken(), delay);
  }
}

/** KC 토큰·타이머 정리(로그아웃/갱신 실패). */
function clearKcTokens(): void {
  setAccessToken(null);
  if (kcRefreshTimer) {
    clearTimeout(kcRefreshTimer);
    kcRefreshTimer = null;
  }
  if (typeof window !== "undefined") window.sessionStorage.removeItem(KC_REFRESH_KEY);
}

/** refresh_token 으로 access_token 재발급. 실패 시 세션 정리 후 false. */
export async function refreshKeycloakToken(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const rt = window.sessionStorage.getItem(KC_REFRESH_KEY);
  if (!rt) return false;
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: OIDC_CONFIG.client_id,
    refresh_token: rt,
  });
  try {
    const res = await fetch(kcTokenEndpoint(), {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });
    if (!res.ok) {
      clearKcTokens();
      return false;
    }
    storeKcTokens((await res.json()) as KcTokenResponse);
    return true;
  } catch {
    clearKcTokens();
    return false;
  }
}

/**
 * ROPC 로그인 — 백엔드 로그인 프록시(POST /api/auth/password-login) 경유(WU12, FR-PLT-RBAC / Part 11 §11.10(d)).
 *
 * <p>기존엔 Keycloak 토큰 엔드포인트를 브라우저에서 직접 호출했으나, 워크스페이스별 로그인 실패 잠금을 집행하려면 백엔드가 로그인 경로에 있어야 함. 백엔드가
 * Keycloak에 자격을 위임 검증하고 실패를 관측·잠금하며, 성공 시 Keycloak 토큰 원본(access_token·refresh_token·expires_in)을 그대로
 * 반환함. 리다이렉트 없음(앱 폼 유지). 토큰 자동 갱신(refreshKeycloakToken)은 기존처럼 Keycloak 직접 호출을 유지함.
 *
 * @throws LoginError 인증 실패(401 자격 불일치 / 403 이메일 미인증 / 423 실패 초과 계정 잠금)
 */
export async function keycloakPasswordLogin(
  username: string,
  password: string,
  totp?: string,
): Promise<SessionUser> {
  const base = import.meta.env.VITE_API_URL ?? "";
  // totp 는 2단계 인증(MFA) 등록 사용자만 입력. 값이 있을 때만 전송(백엔드→Keycloak Direct Grant OTP).
  const body: { email: string; password: string; totp?: string } = { email: username, password };
  if (totp && totp.trim()) body.totp = totp.trim();
  // 네트워크 예외/5xx 는 loginFetch 가 ServerUnavailableError 로 정규화(서버 다운 안내). 4xx 는 통과.
  const res = await loginFetch(`${base}/api/auth/password-login`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await readLoginError(res);
  const data = (await res.json()) as KcTokenResponse & { mfaRequired?: boolean };
  // 비밀번호는 맞으나 2단계 인증 코드가 필요 → 토큰 없이 신호만 옴. FE 가 코드 입력 단계로 전환.
  if (data.mfaRequired) throw new MfaRequiredError();
  storeKcTokens(data);
  const full = await fetchMeAsSessionUser(data.access_token);
  if (!full) throw new LoginError(500);
  return full;
}

/**
 * OIDC User → SessionUser 매핑.
 * Keycloak realm_access.roles / resource_access 에서 역할 추출.
 */
function toSessionUser(user: User): SessionUser {
  const profile = user.profile as Record<string, unknown>;

  const realmAccess = profile["realm_access"] as { roles?: string[] } | undefined;
  const roles = Array.isArray(realmAccess?.roles) ? realmAccess.roles : [];

  // 회사 slug: 커스텀 클레임(company_slug) → 폴백 빈 문자열
  const companySlug =
    typeof profile["company_slug"] === "string" ? (profile["company_slug"] as string) : "";

  return {
    sub: user.profile.sub,
    email: typeof user.profile.email === "string" ? user.profile.email : "",
    name:
      typeof user.profile.name === "string"
        ? user.profile.name
        : typeof user.profile.preferred_username === "string"
          ? (user.profile.preferred_username as string)
          : "",
    roles,
    companySlug,
  };
}

/**
 * 로그인 시작 — Keycloak 로그인 페이지로 리다이렉트.
 * @param returnUrl 로그인 후 복귀할 앱 내 경로 (state 로 보존)
 */
export async function signinRedirect(returnUrl?: string): Promise<void> {
  await getUserManager().signinRedirect({ state: { returnUrl } });
}

/**
 * OIDC 콜백 처리 — 인가 코드를 토큰으로 교환하고 세션 적재.
 * @returns 매핑된 SessionUser 와 로그인 시 전달한 returnUrl
 */
export async function handleCallback(): Promise<{ user: SessionUser; returnUrl?: string }> {
  const oidcUser = await getUserManager().signinRedirectCallback();
  setAccessToken(oidcUser.access_token ?? null);

  const state = oidcUser.state as { returnUrl?: string } | undefined;
  // roleName·positionName·siteAdmin 은 JWT 클레임에 없어 toSessionUser 로는 누락됨.
  // 사이드바 "설정" 게이트가 roleName 을 보므로 누락되면 메뉴가 숨는 불일치가 생김.
  // /auth/me 로 전체 사용자를 복원하고 실패 시에만 토큰 클레임 매핑으로 폴백(dev·ROPC 경로와 동일).
  const full = oidcUser.access_token ? await fetchMeAsSessionUser(oidcUser.access_token) : null;
  return { user: full ?? toSessionUser(oidcUser), returnUrl: state?.returnUrl };
}

/**
 * 현재 세션 사용자 조회 (저장소 복원).
 * - dev 모킹 시: sessionStorage 의 폼 로그인 토큰을 복원하고 /auth/me 로 사용자 정보를 가져옴.
 *   토큰이 없으면(미로그인) null 을 반환해 AuthGuard 가 /login 으로 보냄.
 * - prod: OIDC 세션 복원. 미로그인/만료 시 null.
 */
export async function getUser(): Promise<SessionUser | null> {
  if (DEV_MOCK_AUTH) {
    const token = restoreDevToken();
    if (!token) {
      return null; // 미로그인 — 로그인 폼으로
    }
    // 토큰(= 사용자 ID)으로 BE 현재 사용자 복원. 실패 시 토큰 폐기 후 미로그인 처리.
    try {
      const base = import.meta.env.VITE_API_URL ?? "";
      const response = await fetch(`${base}/api/auth/me`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        setAccessToken(null);
        return null;
      }
      const me = (await response.json()) as {
        id?: string;
        email?: string;
        name?: string;
        roleId?: string | null;
        roleName?: string | null;
        roleCode?: string | null;
        positionName?: string | null;
        companyId?: string | null;
        siteAdmin?: boolean;
        superAdmin?: boolean;
        passwordExpired?: boolean;
      };
      const roles = [me.roleId, me.roleName].filter((r): r is string => Boolean(r));
      return {
        sub: me.id ?? token,
        email: me.email ?? "",
        name: me.name ?? "",
        roles,
        roleName: me.roleName ?? undefined,
        roleCode: me.roleCode ?? undefined,
        positionName: me.positionName ?? undefined,
        companySlug: me.companyId ?? "",
        isSiteAdmin: me.siteAdmin ?? undefined,
        isSuperAdmin: me.superAdmin ?? undefined,
        passwordExpired: me.passwordExpired ?? undefined,
      };
    } catch {
      setAccessToken(null);
      return null;
    }
  }

  if (KEYCLOAK_DIRECT) {
    // 새로고침/재진입: refresh_token 이 세션 진실. 있으면 새 access_token 확보 후 /me 로 사용자 복원.
    if (typeof window === "undefined") return null;
    if (!window.sessionStorage.getItem(KC_REFRESH_KEY)) return null; // 미로그인
    const ok = await refreshKeycloakToken();
    if (!ok) return null;
    const token = getAccessToken();
    const user = token ? await fetchMeAsSessionUser(token) : null;
    if (!user) clearKcTokens();
    return user;
  }

  const oidcUser = await getUserManager().getUser();
  if (!oidcUser || oidcUser.expired) {
    setAccessToken(null);
    return null;
  }
  setAccessToken(oidcUser.access_token ?? null);
  // 세션 복원 시에도 /auth/me 로 전체 사용자(roleName·siteAdmin 등)를 채운다(위 handleCallback 과 동일 이유).
  const full = oidcUser.access_token ? await fetchMeAsSessionUser(oidcUser.access_token) : null;
  return full ?? toSessionUser(oidcUser);
}

/**
 * 로그아웃 — Keycloak 세션 종료 및 로컬 토큰 정리.
 * dev 모킹 시 토큰만 정리.
 */
export async function logout(): Promise<void> {
  setAccessToken(null);
  if (DEV_MOCK_AUTH) {
    return;
  }
  if (KEYCLOAK_DIRECT) {
    clearKcTokens(); // 로컬 토큰만 정리(리다이렉트 없음). Keycloak SSO 쿠키는 dev 편의상 유지.
    return;
  }
  await getUserManager().signoutRedirect();
}
